import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Link, useParams } from "react-router-dom";
import { BtnLoader } from "../../../icons";
import FeedCard from "../../Home/main-content/feed/feed-card";
import { ROUTES } from "../../../constants/routes";
import Button from "../../common/button";
import { preComputeFollowData } from "../../Home/main-content";
import { useLazyGetUserActivityQuery } from "../../../store/api/userApi";
import {
  ActivityTypeDisplayName,
  ActivityType,
  CommentDetail,
  CommentPreview,
} from "../user-profile/activity";

const UserActivityMainContent = ({ tabState }: { tabState?: ActivityType }) => {
  const { user_id } = useParams();
  const observer = useRef<IntersectionObserver | null>(null);
  const [activityData, setActivityData] = useState<any[]>([]);
  const [isEnd, setIsEnd] = useState(false);
  const [trigger, { isFetching }] = useLazyGetUserActivityQuery();
  const [activityType, setActivityType] = useState<ActivityType>(
    tabState || ActivityType.POSTS,
  );

  const memoFollowData = useMemo(
    () => preComputeFollowData(activityData),
    [activityData],
  );
  const [isFollow, setIsFollow] = useState<{ [key: string]: boolean }>({});

  useEffect(() => {
    setIsFollow((prevIsFollow) => {
      const newIsFollow = preComputeFollowData(activityData);
      if (JSON.stringify(newIsFollow) !== JSON.stringify(prevIsFollow)) {
        return newIsFollow;
      }
      return prevIsFollow;
    });
  }, [memoFollowData]);

  useEffect(() => {
    setActivityData([]);
    setIsEnd(false);
    getNewActivityData(true);
  }, [activityType]);

  const getNewActivityData = async (replace: boolean = false) => {
    if (isFetching) return;
    try {
      const response = await trigger(
        {
          user_id,
          offset: replace ? 0 : activityData.length,
          limit: 2,
          type: activityType,
        },
        false,
      )?.unwrap();
      if (response?.success) {
        const newData =
          response?.data?.posts || response?.data?.reactions || [];
        setActivityData((prev) => (replace ? newData : [...prev, ...newData]));
        if (response?.data?.is_end) setIsEnd(true);
      }
    } catch (e) {
      console.log(e);
    }
  };

  const lastPostElementRef = useCallback(
    (node: HTMLDivElement) => {
      if (isFetching) return;
      if (observer.current) observer.current.disconnect();

      observer.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting) {
          getNewActivityData();
        }
      });

      if (node) observer.current.observe(node);
    },
    [isFetching],
  );

  return (
    <div className="lg:col-span-6 h-full mb-4 overflow-y-auto">
      <div className="flex space-x-4">
        {Object.entries(ActivityTypeDisplayName).map(([key, value]) => (
          <button
            key={key}
            onClick={() => setActivityType(key as ActivityType)}
            className={`${
              activityType === key
                ? "bg-[#007AFF] text-white"
                : "border-[#D7D7D7] border-[1px] bg-white text-black"
            } px-4 py-1 rounded-2xl text-sm capitalize`}
          >
            {value}
          </button>
        ))}
      </div>
      <div className="space-y-4 mt-[22px] bg-white p-5 rounded-lg">
        {activityData.length === 0 && (
          <div className="bg-white rounded-lg flex flex-col items-center p-10">
            <img
              src="/assets/logo/bookmark.png"
              alt="empty"
              className="mx-auto w-[204px]"
            />
            <h1 className="text-4xl font-bold my-5">No Bookmarks Yet</h1>
            <h4 className="text-2xl font-light w-3/5 text-center mb-5">
              Start bookmarking posts to save your favorites in one place!
            </h4>
            <Link to={ROUTES.HOME}>
              <Button customClasses="font-semibold rounded-xl text-lg px-12 py-2 m-2">
                Go to Feed
              </Button>
            </Link>
          </div>
        )}
        {activityType === ActivityType.POSTS
          ? activityData.map((post: any, index: number) => (
              <FeedCard
                key={index}
                postData={post}
                follow={isFollow[post.user.id]}
                setIsFollow={(followStatus: boolean) =>
                  setIsFollow((prev) => ({
                    ...prev,
                    [post.user.id]: followStatus,
                  }))
                }
              />
            ))
          : activityData?.map((comment: CommentDetail, index: number) => (
              <CommentPreview
                showBorder={index < activityData.length - 1}
                comment={comment}
              />
            ))}
      </div>
      {!isEnd ? (
        <div
          className="w-full flex justify-center my-4"
          ref={lastPostElementRef}
        >
          {" "}
          <BtnLoader />
        </div>
      ) : null}
    </div>
  );
};

export default UserActivityMainContent;
